import {
  toWSDateString,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { Currency, InvoiceRow } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { selectorSearchRowSecondaryName } from "../../../../shared/selectors/selectorSearchRowSecondaryName";
import { getInvoiceStatusPillProps } from "./getInvoiceStatusPillProps";
import { getSecondaryTextProps } from "./getSecondaryTextProps";

type GetColumnsOptions = {
  // Shown in the Amount header when every row shares one currency.
  currencyCode?: string;
};

export const getColumns = (
  options: GetColumnsOptions
): WSTableColumn<InvoiceRow>[] => [
  {
    config: {
      header: { text: "Client" },
      gridTemplateSizeMax: "2fr"
    },
    renderFunction: ({ data: invoice }) => {
      const name = selectorSearchRowName(invoice);
      return (
        <WSTableCell
          avatar={{ type: "text", text: name }}
          text={name || undefined}
          secondaryText={selectorSearchRowSecondaryName(invoice)}
          truncationOptions={{ text: { allowTwoLines: true } }}
        />
      );
    }
  },
  {
    config: {
      hideOnScreens: ["XS", "S", "M"],
      header: { text: "Invoice #" },
      gridTemplateSizeMin: "min-content",
      gridTemplateSizeMax: "1fr"
    },
    renderFunction: ({ data: invoice }) => (
      <WSTableCell
        text={invoice.invoiceNumber}
        secondaryText={invoice.lineItems?.[0]?.description ?? undefined}
      />
    )
  },
  {
    config: {
      header: {
        text: `Amount ${options.currencyCode ? `(${options.currencyCode})` : ""}`
      }
    },
    renderFunction: ({ data: invoice }) => (
      <WSTableCell
        text={formatMoney(
          invoice.amount ?? 0,
          invoice.currency ?? Currency.USD
        )}
      />
    )
  },
  {
    config: {
      hideOnScreens: ["XS", "S", "M", "L"],
      header: { text: "Opened date" },
      gridTemplateSizeMax: "1fr"
    },
    renderFunction: ({ data: invoice }) => (
      <WSTableCell
        text={
          invoice.events?.openedAt
            ? toWSDateString(invoice.events.openedAt, "monthDayYear")
            : ""
        }
      />
    )
  },
  {
    config: {
      hideOnScreens: ["XS", "S"],
      header: { text: "Due date" },
      gridTemplateSizeMax: "1fr"
    },
    renderFunction: ({ data: invoice }) => (
      <WSTableCell
        text={
          invoice.dueDate ? toWSDateString(invoice.dueDate, "monthDayYear") : ""
        }
      />
    )
  },
  {
    config: {
      hideOnScreens: ["XS"],
      header: { text: "Status" },
      gridTemplateSizeMax: "min-content"
    },
    renderFunction: ({ data: invoice }) => (
      <WSTableCell
        pill={getInvoiceStatusPillProps(invoice)}
        {...getSecondaryTextProps(invoice)}
      />
    )
  }
];
